import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Plus, Sparkles, Trash2, Loader2 } from "lucide-react";
import { cleanChangeOrderTitle } from "@/lib/change-order-text";
import { ScopePhotosUploader, type ScopePhoto } from "@/components/ScopePhotosUploader";

type Contract = {
  id: string;
  number: string;
  title: string;
  company_id: string;
  currency: string;
  amount: number;
  estimate_id: string | null;
  clients: { name: string } | null;
};

type LineItem = {
  description: string;
  quantity: number;
  unit_price: number;
};

export const Route = createFileRoute("/_app/change-orders/new")({
  validateSearch: (s: Record<string, unknown>) => ({
    contractId: typeof s.contractId === "string" ? s.contractId : undefined,
    estimateId: typeof s.estimateId === "string" ? s.estimateId : undefined,
  }),
  component: NewChangeOrderPage,
});

function NewChangeOrderPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, company } = useAuth();
  const { contractId: preselected } = useSearch({ from: Route.id });

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [contractId, setContractId] = useState<string>(preselected ?? "");
  const [title, setTitle] = useState("");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [taxPercent, setTaxPercent] = useState<number>(0);
  const [items, setItems] = useState<LineItem[]>([{ description: "", quantity: 1, unit_price: 0 }]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scopePhotos, setScopePhotos] = useState<ScopePhoto[]>([]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("contracts")
        .select("id, number, title, company_id, currency, amount, estimate_id, clients(name)")
        .in("status", ["signed", "active", "completed"])
        .order("created_at", { ascending: false });
      setContracts((data ?? []) as unknown as Contract[]);
    })();
  }, []);

  const selected = useMemo(
    () => contracts.find((c) => c.id === contractId) ?? null,
    [contracts, contractId],
  );

  const currency = selected?.currency || "USD";
  const formatMoney = (v: number) =>
    new Intl.NumberFormat(i18n.language || "en-US", { style: "currency", currency }).format(v || 0);
  const signedMoney = (v: number) => `${v >= 0 ? "+" : ""}${formatMoney(v)}`;

  const subtotal = items.reduce(
    (s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0),
    0,
  );
  const taxAmount = (subtotal * (taxPercent || 0)) / 100;
  const total = subtotal + taxAmount;
  const originalTotal = Number(selected?.amount) || 0;
  const revisedTotal = originalTotal + total;

  const updateItem = (idx: number, patch: Partial<LineItem>) =>
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((p) => [...p, { description: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (idx: number) =>
    setItems((p) => (p.length === 1 ? p : p.filter((_, i) => i !== idx)));

  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error(t("pages.changeOrders.new.errorDescribe"));
      return;
    }
    setGenerating(true);
    try {
      const clientName = selected?.clients?.name;
      const context = [
        selected
          ? t("pages.changeOrders.new.aiContextEstimate", {
              number: selected.number,
              title: selected.title,
            })
          : null,
        clientName ? t("pages.changeOrders.new.aiContextClient", { name: clientName }) : null,
        t("pages.changeOrders.new.aiContextDeductions"),
      ]
        .filter(Boolean)
        .join(". ");
      const aiLanguage = i18n.language?.startsWith("es")
        ? "es"
        : i18n.language?.startsWith("en")
          ? "en"
          : "pt";
      const { data, error } = await supabase.functions.invoke("generate-estimate-items", {
        body: { description: aiPrompt, currency, context, language: aiLanguage },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.title && !title) {
        const cleaned = cleanChangeOrderTitle(String(data.title));
        setTitle(cleaned || String(data.title));
      }
      if (data?.summary && !description) setDescription(String(data.summary));
      if (Array.isArray(data?.items) && data.items.length) {
        setItems(
          data.items.map((it: LineItem) => ({
            description: String(it.description || ""),
            quantity: Number(it.quantity) || 1,
            unit_price: Number(it.unit_price) || 0,
          })),
        );
      }
      toast.success(t("pages.changeOrders.new.aiReady"));
    } catch (e) {
      const msg = e instanceof Error ? e.message : t("pages.changeOrders.new.aiFailedDefault");
      toast.error(t("pages.changeOrders.new.aiFailed"), { description: msg });
    } finally {
      setGenerating(false);
    }
  };

  const save = async (status: "draft" | "sent") => {
    if (!contractId || !selected) {
      toast.error(t("pages.changeOrders.new.selectEstimateError"));
      return;
    }
    if (!title.trim()) {
      toast.error(t("pages.changeOrders.new.titleError"));
      return;
    }
    const cleanItems = items.filter((i) => i.description.trim());
    if (cleanItems.length === 0) {
      toast.error(t("pages.changeOrders.new.errorItem"));
      return;
    }
    if (!user?.id) {
      toast.error(t("pages.changeOrders.new.sessionError"));
      return;
    }
    setSaving(true);
    try {
      const finalPhotos = scopePhotos
        .filter((p) => p.path && !p.uploading)
        .map((p) => ({ path: p.path, ...(p.caption?.trim() ? { caption: p.caption.trim() } : {}) }));
      const { data: co, error } = await supabase
        .from("change_orders")
        .insert({
          contract_id: contractId,
          estimate_id: selected.estimate_id ?? null,
          company_id: selected.company_id,
          currency: selected.currency,
          title: title.trim(),
          number: "",
          reason: reason.trim() || null,
          description: description.trim() || null,
          tax_percent: taxPercent,
          status,
          created_by: user.id,
          scope_photos: finalPhotos,
        })
        .select("id")
        .single();
      if (error || !co) throw error ?? new Error(t("pages.changeOrders.new.createFail"));

      const { error: iErr } = await supabase.from("change_order_items").insert(
        cleanItems.map((it, idx) => ({
          change_order_id: co.id,
          description: it.description.trim(),
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          sort_order: idx,
        })),
      );
      if (iErr) throw iErr;

      toast.success(t("pages.changeOrders.new.created"));
      navigate({ to: "/change-orders/$changeOrderId", params: { changeOrderId: co.id } });
    } catch (e) {
      const msg = e instanceof Error ? e.message : t("pages.changeOrders.new.createFail");
      toast.error(t("pages.changeOrders.new.createFail"), { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: "/change-orders" })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("pages.changeOrders.new.title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("pages.changeOrders.new.subtitle")}</p>
        </div>
      </div>

      {contracts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center">
          <h2 className="text-lg font-semibold">{t("pages.changeOrders.new.noContracts")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("pages.changeOrders.new.noContractsDesc")}
          </p>
          <Button asChild className="mt-4">
            <Link to="/contracts">{t("pages.changeOrders.new.goToContracts")}</Link>
          </Button>
        </div>
      ) : (
        <>
          {/* AI block */}
          <div
            className="rounded-lg border border-border p-5"
            style={{ background: "var(--gradient-subtle, var(--card))" }}
          >
            <div className="mb-3 flex items-center gap-2">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-md text-primary-foreground"
                style={{ background: "var(--gradient-brand)" }}
              >
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold">{t("pages.changeOrders.new.aiTitle")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("pages.changeOrders.new.aiHint")}
                </p>
              </div>
            </div>
            <Textarea
              rows={3}
              placeholder={t("pages.changeOrders.new.aiPlaceholder")}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className="bg-background"
            />
            <div className="mt-3 flex justify-end">
              <Button onClick={generateWithAI} disabled={generating} className="gap-2">
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {generating
                  ? t("pages.changeOrders.new.generating")
                  : t("pages.changeOrders.new.generate")}
              </Button>
            </div>
          </div>

          {/* Fields */}
          <div className="grid gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("pages.changeOrders.new.approvedContract")} *</Label>
              <Select value={contractId} onValueChange={setContractId}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.changeOrders.new.selectContract")} />
                </SelectTrigger>
                <SelectContent>
                  {contracts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.number} — {c.title}
                      {c.clients?.name ? ` · ${c.clients.name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="co-title">{t("pages.changeOrders.new.titleField")} *</Label>
              <Input
                id="co-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("pages.changeOrders.new.titlePlaceholder")}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="co-reason">{t("pages.changeOrders.detail.reason")}</Label>
              <Input
                id="co-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("pages.changeOrders.new.reasonPlaceholder")}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="co-desc">{t("pages.changeOrders.detail.description")}</Label>
              <Textarea
                id="co-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("pages.estimates.form.scopePhotos")}</Label>
              <ScopePhotosUploader
                bucket="change-order-scope-photos"
                companyId={company?.id}
                photos={scopePhotos}
                setPhotos={setScopePhotos}
              />
            </div>

            <div className="space-y-1.5">
              <Label>{t("pages.changeOrders.new.tax")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={taxPercent}
                onChange={(e) => setTaxPercent(Number(e.target.value) || 0)}
              />
            </div>
          </div>

          {/* Line items */}
          <div className="rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border p-4">
              <div>
                <p className="text-sm font-semibold">{t("pages.changeOrders.new.lineItems")}</p>
                <p className="text-xs text-muted-foreground">
                  {t("pages.changeOrders.new.itemsHint")}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={addItem} className="gap-2">
                <Plus className="h-3.5 w-3.5" />
                {t("pages.changeOrders.new.addItem")}
              </Button>
            </div>

            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pages.changeOrders.new.description")}</TableHead>
                    <TableHead className="w-24">{t("pages.changeOrders.new.qty")}</TableHead>
                    <TableHead className="w-32">{t("pages.changeOrders.new.unitPrice")}</TableHead>
                    <TableHead className="w-32 text-right">
                      {t("pages.changeOrders.new.total")}
                    </TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it, idx) => {
                    const lineTotal = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <Input
                            value={it.description}
                            onChange={(e) => updateItem(idx, { description: e.target.value })}
                            placeholder={t("pages.changeOrders.new.itemPlaceholder")}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            value={it.quantity}
                            onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={it.unit_price}
                            onChange={(e) =>
                              updateItem(idx, { unit_price: Number(e.target.value) })
                            }
                          />
                        </TableCell>
                        <TableCell
                          className={`text-right text-sm font-medium ${lineTotal < 0 ? "text-rose-600" : ""}`}
                        >
                          {signedMoney(lineTotal)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(idx)}
                            disabled={items.length === 1}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mobile */}
            <div className="md:hidden divide-y divide-border">
              {items.map((it, idx) => {
                const lineTotal = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                return (
                  <div key={idx} className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">
                        {t("pages.changeOrders.new.itemNumber", { number: idx + 1 })}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 -mr-2"
                        onClick={() => removeItem(idx)}
                        disabled={items.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("pages.changeOrders.new.description")}</Label>
                      <Textarea
                        rows={2}
                        value={it.description}
                        onChange={(e) => updateItem(idx, { description: e.target.value })}
                        placeholder={t("pages.changeOrders.new.itemPlaceholder")}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("pages.changeOrders.new.qty")}</Label>
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          value={it.quantity}
                          onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("pages.changeOrders.new.unitPrice")}</Label>
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={it.unit_price}
                          onChange={(e) => updateItem(idx, { unit_price: Number(e.target.value) })}
                        />
                      </div>
                    </div>
                    <div
                      className={`flex items-center justify-between rounded-md bg-muted/50 px-3 py-2 ${lineTotal < 0 ? "text-rose-600" : ""}`}
                    >
                      <span className="text-xs text-muted-foreground">
                        {t("pages.changeOrders.new.total")}
                      </span>
                      <span className="text-sm font-semibold">{signedMoney(lineTotal)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-end gap-1 border-t border-border p-4 text-sm">
              <div className="flex w-64 justify-between">
                <span className="text-muted-foreground">
                  {t("pages.changeOrders.new.subtotal")}
                </span>
                <span>{signedMoney(subtotal)}</span>
              </div>
              <div className="flex w-64 justify-between">
                <span className="text-muted-foreground">
                  {t("pages.changeOrders.new.taxLabel", {
                    percent: taxPercent,
                  })}
                </span>
                <span>{signedMoney(taxAmount)}</span>
              </div>
              <div
                className={`mt-1 flex w-64 justify-between border-t border-border pt-1 text-base font-semibold ${total < 0 ? "text-rose-600" : "text-emerald-600"}`}
              >
                <span>{t("pages.changeOrders.new.deltaTotal")}</span>
                <span>{signedMoney(total)}</span>
              </div>
              {selected && (
                <div className="mt-2 flex w-64 flex-col gap-0.5 border-t border-border pt-2 text-xs text-muted-foreground">
                  <div className="flex justify-between">
                    <span>{t("pages.changeOrders.detail.original")}</span>
                    <span>{formatMoney(originalTotal)}</span>
                  </div>
                  <div className="flex justify-between font-medium text-foreground">
                    <span>{t("pages.changeOrders.detail.revised")}</span>
                    <span>{formatMoney(revisedTotal)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => save("draft")} disabled={saving}>
              {t("pages.changeOrders.new.saveDraft")}
            </Button>
            <Button onClick={() => save("sent")} disabled={saving}>
              {saving ? t("pages.changeOrders.new.saving") : t("pages.changeOrders.new.saveSent")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
